/**
 * Firebase singletons for the customer app.
 *
 * - App, Auth, Firestore initialized once.
 * - Firestore uses a persistent local cache (offline persistence) so cached
 *   prices survive reloads and are available offline (feature #6).
 * - Messaging is created lazily and ONLY on web where it is supported; on
 *   native Android FCM is handled by @capacitor/push-notifications, not the
 *   web Messaging SDK. See lib/push.ts.
 * - Emulator wiring is guarded by VITE_USE_EMULATORS.
 */
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  type Auth,
} from 'firebase/auth';
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore';
import {
  getMessaging,
  isSupported as isMessagingSupported,
  type Messaging,
} from 'firebase/messaging';

import { firebaseConfig, useEmulators } from './env';
import { isWeb } from './platform';

export const firebaseApp: FirebaseApp = initializeApp(firebaseConfig);

export const auth: Auth = getAuth(firebaseApp);

/**
 * Persistent multi-tab cache = offline persistence for the modern SDK.
 * `initializeFirestore` (not `getFirestore`) is required to pass cache config.
 */
export const db: Firestore = initializeFirestore(firebaseApp, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});

// FREE-TIER MODEL: no Cloud Functions on the Spark plan. All privileged writes
// are gone; the app talks only to Auth + Firestore (gated by security rules).
if (useEmulators) {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
}

let messagingInstance: Messaging | null = null;
let messagingChecked = false;

/**
 * Returns a web Messaging instance when supported, else null.
 * Native push is not routed through this — see lib/push.ts.
 */
export async function getWebMessaging(): Promise<Messaging | null> {
  if (!isWeb) return null;
  if (messagingChecked) return messagingInstance;
  messagingChecked = true;
  try {
    const supported = await isMessagingSupported();
    if (!supported) return null;
    messagingInstance = getMessaging(firebaseApp);
    return messagingInstance;
  } catch {
    return null;
  }
}
